{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "analytics",
  "x-cli-editable": true,
  "x-tier": "advanced",
  "title": "Analytics",
  "description": "Tracking and classification: UTM settings, topic taxonomy, form tracking pages, parent window event forwarding.",
  "type": "object",
  "properties": {
    "session_utm_name": {
      "ui:sectionTitle": "UTM Tracking",
      "ui:sectionDescription": "Custom UTM parameter names Rose uses for attribution.",
      "ui:sectionIcon": "Tag",
      "type": [
        "string",
        "null"
      ],
      "ui:description": "Custom UTM parameter name for session tracking",
      "maxLength": 100
    },
    "visitor_utm_name": {
      "type": [
        "string",
        "null"
      ],
      "ui:description": "Custom UTM parameter name for visitor tracking",
      "maxLength": 100
    },
    "login_urls": {
      "ui:sectionTitle": "Page Tracking",
      "ui:sectionDescription": "Which pages Rose watches for logins, forms, conversions, and key intents.",
      "ui:sectionIcon": "Target",
      "type": "array",
      "ui:description": "URLs that indicate a logged-in user",
      "maxItems": 50,
      "items": {
        "type": "string"
      }
    },
    "taxonomy": {
      "type": "object",
      "ui:description": "Topic classification taxonomy",
      "properties": {
        "topics": {
          "type": "array",
          "ui:description": "Topic definitions for conversation classification",
          "maxItems": 100,
          "items": {
            "type": "object",
            "properties": {
              "description": {
                "type": "string",
                "ui:description": "Description to help LLM identify this topic",
                "maxLength": 500,
                "minLength": 1
              },
              "id": {
                "type": "string",
                "ui:description": "Unique topic identifier (e.g., 'pricing', 'features')",
                "maxLength": 50,
                "minLength": 1
              },
              "name": {
                "type": "string",
                "ui:description": "Human-readable topic name (e.g., 'Pricing & Costs')",
                "maxLength": 100,
                "minLength": 1
              },
              "examples": {
                "type": "array",
                "ui:description": "Example questions/phrases for this topic",
                "items": {
                  "type": "string"
                }
              }
            },
            "required": [
              "id",
              "name",
              "description"
            ]
          }
        }
      }
    },
    "form_tracking_pages": {
      "type": "array",
      "ui:description": "URL patterns of pages with forms to track. To match query params or hashes, include ? or # explicitly in the pattern.",
      "maxItems": 50,
      "items": {
        "type": "string"
      }
    },
    "thank_you_pages": {
      "type": "array",
      "ui:description": "URL patterns for thank-you/confirmation pages (conversion tracking). To match query params or hashes, include ? or # explicitly in the pattern.",
      "maxItems": 50,
      "items": {
        "type": "string"
      }
    },
    "conversion_filters": {
      "type": "object",
      "ui:description": "Restricts which detected form submissions count as Rose conversions. Without filters, every form on form_tracking_pages fires rw_client_form_submitted — including newsletter or search forms on the same page. Each filter applies whenever the submission carries the value it checks: a form embedded through HubSpot, Typeform, Calendly or your data layer reports the provider's own form id, so that id has to be in form_id_whitelist for the submission to count. Detections that identify no specific form — thank-you pages, and submissions Rose infers from page or network activity — are matched on url_include / url_exclude only.",
      "properties": {
        "form_id_whitelist": {
          "type": "array",
          "ui:description": "Only forms whose HTML id attribute is in this list count as conversions. Leave empty to track every form on form_tracking_pages. Example: [demo_form] tracks only the form with id=demo_form and ignores other forms on the same page (e.g. newsletter signup).",
          "maxItems": 50,
          "items": {
            "type": "string"
          }
        },
        "form_action_whitelist": {
          "type": "array",
          "ui:description": "Only forms that POST to one of these URLs count as conversions. Leave empty to skip this check. Use when conversion forms share a submit endpoint but the page hosts other forms too (e.g. only forms posting to https://start.example.com/users count).",
          "maxItems": 50,
          "items": {
            "type": "string"
          }
        },
        "url_include": {
          "type": "array",
          "ui:description": "The page URL must contain one of these text fragments for the submission to count. Leave empty to skip this check. Example: [/sign_up] only counts forms submitted from a sign-up page, ignoring logins or event registrations on other URLs.",
          "maxItems": 50,
          "items": {
            "type": "string"
          }
        },
        "url_exclude": {
          "type": "array",
          "ui:description": "Submissions from a page URL containing any of these text fragments never count, even when the other filters pass. Example: [capital_deposit] drops a deposit form that lives on an otherwise-tracked sign-up flow.",
          "maxItems": 50,
          "items": {
            "type": "string"
          }
        }
      },
      "default": {
        "form_id_whitelist": [],
        "form_action_whitelist": [],
        "url_include": [],
        "url_exclude": []
      }
    },
    "conversion_labels": {
      "ui:sectionTitle": "Conversion labels",
      "ui:sectionDescription": "Group tracked conversions into labels such as Signup, Demo, or a custom outcome. Conversions that do not match a rule stay labeled Conversion.",
      "ui:sectionIcon": "Tags",
      "ui:widget": "conversion-labels",
      "type": "array",
      "ui:description": "Rules are checked in order. The first matching label is used for each conversion event.",
      "maxItems": 20,
      "items": {
        "type": "object",
        "properties": {
          "label": {
            "type": "string",
            "ui:description": "Name shown in analytics, for example Signup, Demo, Webinar, or Contact sales.",
            "maxLength": 80,
            "minLength": 1,
            "pattern": "^(?!\\s*(?:[Cc][Oo][Nn][Vv][Ee][Rr][Ss][Ii][Oo][Nn]|[Oo][Tt][Hh][Ee][Rr]|[Ee][Mm][Aa][Ii][Ll]\\s+[Oo][Nn][Ll][Yy])\\s*$).+"
          },
          "count_as_conversion": {
            "type": "boolean",
            "default": true,
            "ui:description": "Keep on for real conversions (Signup, Demo). Turn off to tag submissions you do not want counted as conversions, such as newsletter or event-registration forms. They still appear in the breakdown under Excluded, but are removed from your Conversions total."
          },
          "form_ids": {
            "type": "array",
            "ui:description": "Exact submitted form IDs to assign to this label. Pick IDs observed below or paste one from your tracking event.",
            "maxItems": 100,
            "items": { "type": "string" }
          },
          "page_url_patterns": {
            "type": "array",
            "ui:description": "Thank-you or confirmation page URL patterns for conversions without a form ID. Use * as a wildcard.",
            "maxItems": 50,
            "items": { "type": "string" }
          },
          "detection_methods": {
            "type": "array",
            "ui:description": "Optional conversion detection methods, for example thankyou_page_detected or post_message. Combine with a URL pattern when possible.",
            "maxItems": 20,
            "items": { "type": "string" }
          }
        },
        "required": ["label"]
      },
      "default": []
    },
    "intent_pages": {
      "type": "object",
      "ui:tooltip": "Pages that signal a visitor reached a key intent, like your pricing page.",
      "ui:description": "Mark which pages count as reaching a key intent (e.g. your pricing page). Experiments use this to measure how many visitors reach these pages — a much faster signal than waiting for form submissions. Give each intent a name and list the page patterns under it. Patterns match the page path only (the part after your domain); query strings are ignored. Use * as a wildcard. Example: a 'pricing' intent with the patterns /pricing, /pricing/*, /tarifs matches your pricing page, any page beneath it, and your French pricing page.",
      "additionalProperties": {
        "type": "array",
        "maxItems": 50,
        "items": {
          "type": "string"
        }
      }
    },
    "stats_valid_from": {
      "ui:sectionTitle": "Advanced",
      "ui:sectionDescription": "Staff and advanced tracking controls.",
      "ui:sectionIcon": "SlidersHorizontal",
      "type": [
        "string",
        "null"
      ],
      "format": "date-time",
      "x-access": "staff",
      "ui:tooltip": "Set this to today's date (or the time you fixed the tracking config) so historical false-positive events stop polluting backoffice metrics.",
      "ui:description": "Backoffice metric calculation start date for this domain. Use this when a tracking config change (form_detection_strategies, form_tracking_pages, thank_you_pages, conversion_filters) was made recently and pre-fix events are still polluting the 30-day MV window. Pre-fix events are excluded from engagement / conversion / demo / email-only counts. Leave empty to use the full 30-day window. Format: ISO 8601 timestamp (e.g. 2026-04-20T07:38:52Z)."
    },
    "posthog_url": {
      "type": [
        "string",
        "null"
      ],
      "format": "uri",
      "ui:description": "URL to the PostHog dashboard for this site (shown as a shortcut link in the backoffice)",
      "x-access": "staff",
      "pattern": "\\.[a-zA-Z]{2,}"
    },
    "page_load_event_suppression": {
      "type": "string",
      "enum": [
        "off",
        "init_and_pageleave",
        "out_of_scope_hosts"
      ],
      "default": "out_of_scope_hosts",
      "x-access": "staff",
      "ui:tooltip": "Reduces PostHog ingestion cost by not emitting page-load events on pages where the widget cannot appear.",
      "ui:description": "Controls which analytics events are skipped on pages the widget is not allowed to display on. Tracking of CTA clicks and form submissions is never affected. off: emit everything (current behaviour). init_and_pageleave: skip the widget init and page-exit events on those pages, keep the page view. out_of_scope_hosts: additionally skip the page view on subdomains that appear nowhere in Display Only on URLs (for example a logged-in product app), unless the page is used for form or CTA tracking."
    },
    "parent_window_events": {
      "type": "object",
      "ui:description": "Event forwarding to parent window (for analytics integration)",
      "properties": {
        "enabled": {
          "type": "boolean",
          "ui:description": "Enable event forwarding to parent window"
        }
      }
    },
    "form_detection_strategies": {
      "type": "object",
      "ui:description": "Toggle individual form detection strategies on/off per client",
      "properties": {
        "superform": {
          "type": "boolean",
          "ui:description": "Detects Webflow Superform multi-step forms. Tracks completion when the final step becomes visible (sf:complete event or step9 attribute)."
        },
        "hubspot": {
          "type": "boolean",
          "ui:description": "Detects HubSpot embedded forms (hsfc-Form, hs-form classes). These React-based forms don't fire native submit events — detection uses DOM replacement observation."
        },
        "cta_page_form": {
          "type": "boolean",
          "ui:description": "Tracks standard HTML <form> submissions on pages matching form_tracking_pages patterns. Listens for submit events and detects success/failure via page heuristics."
        },
        "thankyou_page": {
          "type": "boolean",
          "ui:description": "Fires a conversion event when the visitor lands on a URL matching thank_you_pages patterns. No form element required — works purely on URL matching."
        },
        "network_observer": {
          "type": "boolean",
          "ui:description": "Detects form submissions on SPA pages that use input fields without a <form> element (Angular, React, Vue). Intercepts fetch/XHR POST requests triggered after a submit-button click."
        },
        "dom_observer": {
          "type": "boolean",
          "ui:description": "Detects form submissions by watching for the email input to disappear from the DOM after a submit button click. Designed for SPA multi-step forms where step transitions remove inputs without a backend POST. Disabled by default."
        },
        "dom_observer_track_wrapped_forms": {
          "type": "boolean",
          "x-tier": "advanced",
          "ui:description": "EXPERIMENTAL — not finished, does not yet fire conversions reliably. Prefer thank_you_pages when the form redirects to a success URL. Requires dom_observer. By default the DOM observer only watches inputs that sit OUTSIDE a form element. This option also treats inputs inside a submit-less form as trackable and watches an external submit button (e.g. the PayFit welcome funnel shape). Known gaps on multi-step SPA funnels: the success heuristic (email input disappears) misfires, and step-navigation buttons can be picked up. Leave off unless a diagnosis shows this exact structure AND there is no success URL to use with thank_you_pages."
        },
        "post_message": {
          "type": "boolean",
          "ui:description": "Detects form submissions from cross-origin iframe embeds (HubSpot, Cal.com, Typeform) by listening for window.postMessage events. These providers fire events when forms are submitted inside their iframes."
        },
        "data_layer": {
          "type": "boolean",
          "ui:description": "Detects form submissions via window.dataLayer.push events (Google Tag Manager). Catches HubSpot's standard 'hubspot-form-success' event used by the new embed-components-app iframe, and any custom GTM events listed in dataLayer_conversion_events."
        }
      }
    },
    "dataLayer_conversion_events": {
      "type": "array",
      "x-tier": "advanced",
      "ui:description": "Additional GTM dataLayer event names that signal a successful conversion. The built-in 'hubspot-form-success' is always matched. Entries may be a plain event name (e.g. 'contact-form-success', 'lead_MQL') or an object {event, where} that additionally requires every key in 'where' to equal the corresponding property on the pushed dataLayer object — use this when one event name is reused for multiple conversions and only a property like 'eventAction' distinguishes them (e.g. {event: 'formSubmissionSuccess', where: {eventAction: 'Demo Requests'}}).",
      "items": {
        "oneOf": [
          {
            "type": "string"
          },
          {
            "type": "object",
            "required": ["event"],
            "additionalProperties": false,
            "properties": {
              "event": {
                "type": "string",
                "ui:description": "GTM dataLayer event name to match (e.g. 'formSubmissionSuccess')."
              },
              "where": {
                "type": "object",
                "ui:description": "Required equality match against properties on the pushed dataLayer object. All keys must be present and equal for the event to count as a conversion. Values must be strings.",
                "additionalProperties": {
                  "type": "string"
                }
              }
            }
          }
        ]
      }
    },
    "post_request_capture": {
      "type": "object",
      "ui:description": "Capture outbound POST activity on selected pages for internal conversion-request discovery",
      "x-access": "staff",
      "properties": {
        "enabled": {
          "type": "boolean",
          "ui:description": "Enable POST request capture discovery on matching pages"
        },
        "redact_payload_values": {
          "type": "boolean",
          "ui:description": "Redact captured payload values before sending them to internal POST discovery storage"
        },
        "page_patterns": {
          "type": "array",
          "ui:description": "Page URL patterns where POST discovery should run. To match query params or hashes, include ? or # explicitly in the pattern.",
          "maxItems": 50,
          "items": {
            "type": "string"
          }
        }
      }
    },
    "internal_ip_filter": {
      "type": "object",
      "ui:tooltip": "Hide visits from your team's IP addresses so internal browsing doesn't inflate your dashboard stats.",
      "ui:description": "Exclude traffic from your team's IP addresses so internal visits (office, home office, VPN) don't count in your dashboard stats.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "ui:tooltip": "Turn the filter on or off without deleting your IP list.",
          "ui:description": "When on, visits from the IPs below are excluded from all dashboard stats. Turning it off keeps your list and counts new visitors from these IPs. Visitors already seen from them stay excluded."
        },
        "excluded_ips": {
          "type": "array",
          "maxItems": 100,
          "ui:description": "One IP per row. Add a label so you remember what each one is (e.g. 'Paris office', 'Johary home VPN'). IPv4 and IPv6 are both accepted. For ranges, list each IP individually.",
          "items": {
            "type": "object",
            "required": [
              "label",
              "ip"
            ],
            "properties": {
              "label": {
                "type": "string",
                "minLength": 1,
                "maxLength": 100,
                "ui:description": "Short name to remember this IP (e.g. 'Paris office')."
              },
              "ip": {
                "type": "string",
                "minLength": 1,
                "maxLength": 45,
                "ui:description": "IPv4 or IPv6 address. Paste the IP exactly as it appears (e.g. 203.0.113.42 or 2001:db8::1)."
              }
            }
          }
        }
      }
    }
  },
  "default": {
    "session_utm_name": "rose_session_id",
    "visitor_utm_name": "rose_visitor_id",
    "login_urls": [],
    "taxonomy": {
      "topics": [
        {
          "id": "pricing",
          "name": "Pricing & Plans",
          "description": "Questions about pricing, costs, plans, discounts, or billing"
        },
        {
          "id": "demo-request",
          "name": "Demo Request",
          "description": "Requests for a product demo, trial, or live walkthrough"
        },
        {
          "id": "product-capabilities",
          "name": "Product Capabilities",
          "description": "Questions about features, functionality, and what the product can do"
        },
        {
          "id": "use-cases",
          "name": "Use Cases",
          "description": "Questions about how the product applies to specific industries, roles, or scenarios"
        },
        {
          "id": "getting-started",
          "name": "Getting Started",
          "description": "Questions about onboarding, setup, implementation, or first steps"
        },
        {
          "id": "integration",
          "name": "Integrations & Technical Fit",
          "description": "Questions about integrations, APIs, compatibility, and technical requirements"
        },
        {
          "id": "competitors",
          "name": "Competitor Comparison",
          "description": "Comparisons with alternative solutions or competitors"
        },
        {
          "id": "roi-value",
          "name": "ROI & Business Value",
          "description": "Questions about return on investment, business impact, and value proposition"
        },
        {
          "id": "security-compliance",
          "name": "Security & Compliance",
          "description": "Questions about security practices, certifications, data privacy, and regulatory compliance"
        },
        {
          "id": "enterprise",
          "name": "Enterprise Needs",
          "description": "Enterprise-specific questions about scale, SLAs, dedicated support, or custom solutions"
        },
        {
          "id": "support-issue",
          "name": "Support Issue",
          "description": "Existing customer support requests, bug reports, or troubleshooting"
        },
        {
          "id": "partnership",
          "name": "Partnership Inquiry",
          "description": "Partnership, reseller, or business development inquiries"
        },
        {
          "id": "other",
          "name": "Other",
          "description": "Topics that do not fit any other category"
        },
        {
          "id": "spam",
          "name": "Spam",
          "description": "Irrelevant, automated, or spam messages"
        }
      ]
    },
    "form_tracking_pages": [],
    "thank_you_pages": [],
    "conversion_filters": {
      "form_id_whitelist": [],
      "form_action_whitelist": [],
      "url_include": [],
      "url_exclude": []
    },
    "intent_pages": {},
    "stats_valid_from": null,
    "posthog_url": null,
    "page_load_event_suppression": "out_of_scope_hosts",
    "form_detection_strategies": {
      "superform": true,
      "hubspot": true,
      "cta_page_form": true,
      "thankyou_page": true,
      "network_observer": true,
      "dom_observer": false,
      "dom_observer_track_wrapped_forms": false,
      "post_message": true,
      "data_layer": true
    },
    "dataLayer_conversion_events": [],
    "parent_window_events": {
      "enabled": true
    },
    "post_request_capture": {
      "enabled": false,
      "redact_payload_values": true,
      "page_patterns": []
    },
    "internal_ip_filter": {
      "enabled": false,
      "excluded_ips": []
    }
  }
}
